import React from 'react';
import {BreakpointID} from '@packages/systems/style/types';
import {defaultCanvasBodyProps} from '../constants';
import {useReportReceivedRequiredProjectData} from '../hooks/useReportReceivedProjectData';
import {ErrorBoundary} from './ErrorBoundary';
import {dispatchInDesigner} from '../utils/dispatchInDesigner';
import {useBodyEventHandlers} from '../hooks/useBodyEventHandlers';
import {
  type CanvasScrollStateShape,
  type CanvasBodyProps,
  buildPreviewMode,
  telemetrySendError,
} from '@packages/systems/designer/canvas';
import {type UiNodeStateType} from '@packages/systems/core/records/UiNodeState';
import {type DesignerWorkflow} from '@packages/systems/designer/workflows';
import {buildIdAttrMap} from '@packages/utilities/platform';
// See epic https://webflow.atlassian.net/browse/DESUX-8149 to pay off this 👇 tech debt
/* eslint-disable webflow/package-boundaries */
import CanvasBody from '../../../../public/js/designer-flux/components/CanvasBody';
/* eslint-enable webflow/package-boundaries */

type CanvasBodyWithDefaultsProps = Omit<
  Partial<CanvasBodyProps>,
  'bodyEventHandlers'
> & {
  components: CanvasBodyProps['components'];
  pageId: CanvasBodyProps['pageId'];
  plugins: CanvasBodyProps['plugins'];
  assets: CanvasBodyProps['assets'];
  ix3ElementTargets: CanvasBodyProps['ix3ElementTargets'];
  ix2NodeMap: CanvasBodyProps['ix2NodeMap'];
  loadedHeadScripts: CanvasBodyProps['loadedHeadScripts'];
  variantDataMap: CanvasBodyProps['variantDataMap'];
  variables: CanvasBodyProps['variables'];
  variableCollections: CanvasBodyProps['variableCollections'];
  stylesMap: CanvasBodyProps['stylesMap'];
  styleBlocks: CanvasBodyProps['styleBlocks'];
  breakpoints: CanvasBodyProps['breakpoints'];
  breakpointId: BreakpointID;
  apolloClient: CanvasBodyProps['apolloClient'];
  apolloCacheResetCounter: CanvasBodyProps['apolloCacheResetCounter'];
  canvasScrollState: CanvasScrollStateShape;
  canvasResizeState: {width: number; height: number; scale: number};
  isOpenCanvasActive: boolean;
  uiNodeState: UiNodeStateType;
  workflow: DesignerWorkflow;
  ixState: CanvasBodyProps['ixState'];
  paginationPageNumbers: CanvasBodyProps['paginationPageNumbers'];
  collections: CanvasBodyProps['collections'];
  dynamoData: CanvasBodyProps['dynamoData'];
  entitlements: CanvasBodyProps['entitlements'];
  componentLocalizations: CanvasBodyProps['componentLocalizations'];
};

export const CanvasBodyWithDefaults = ({
  apolloClient,
  apolloCacheResetCounter,
  assets,
  canvasScrollState,
  canvasResizeState,
  isOpenCanvasActive,
  classNameMap,
  collectionId,
  collectionIdToPageIdMap,
  collections,
  components,
  cssPropertyMap,
  currentItemId,
  currentLocale,
  customNodeState,
  databaseId,
  ecommPublishableKey,
  googleMapsApiKey,
  hrefMap,
  isIndexed,
  ix3ElementTargets,
  ix2NodeMap,
  loadedHeadScripts,
  locales,
  currentVariationId,
  pageHref,
  pageId,
  paginationPageNumbers,
  plugins,
  publicationId,
  recaptchaRequired,
  recaptchaSiteKey,
  rootExpression,
  shortName,
  siteBasePath,
  siteId,
  stylesMap,
  styleBlocks,
  breakpoints,
  breakpointId,
  timezone,
  uiNodeState,
  variantDataMap,
  variantState,
  variables,
  variableCollections,
  workflow,
  ixState,
  dynamoData,
  entitlements,
  codeComponentVersion,
  functionsPreview,
  frameOrientation,
  isComponentCanvasActive,
  globalComponentIds,
  annotationsMode,
  quickCheckoutEnabled,
  ecommShippingZones,
  ecommBusinessAddress,
  ecommCurrencySettings,
  cmsCollectionListPreviewEnabled,
  cmsCollectionListPreviewItemsCount,
  cmsNestedCollectionListPreviewItemsCount,
  componentLocalizations,
}: CanvasBodyWithDefaultsProps) => {
  useReportReceivedRequiredProjectData(
    {
      siteId,
      rootExpression,
      shortName,
      cssPropertyMap,
      classNameMap,
      isIndexed,
      pageHref,
      timezone,
      collections,
      hrefMap,
      paginationPageNumbers,
    },
    'canvasBody'
  );

  const bodyEventHandlers = useBodyEventHandlers({
    canvasScrollState,
    pageId,
    canvasResizeState,
    isOpenCanvasActive,
    uiNodeState,
    workflow,
    components,
    siteBasePath,
    paginationPageNumbers,
  });

  React.useEffect(() => {
    if (googleMapsApiKey) {
      window.GOOGLE_MAPS_API_KEY = googleMapsApiKey;
    }
  }, [googleMapsApiKey]);

  // Wait till we receive all expected user project data from the Designer App before rendering
  // (not the most readability friendly way to check, but works better with TypeScript)
  if (
    siteId === undefined ||
    rootExpression === undefined ||
    shortName === undefined ||
    cssPropertyMap === undefined ||
    classNameMap === undefined ||
    isIndexed === undefined ||
    pageHref === undefined ||
    timezone === undefined ||
    hrefMap === undefined ||
    currentVariationId === undefined
  ) {
    return null;
  }

  const canvasBodyProps: Omit<CanvasBodyProps, 'customCodeExecutionAllowed'> = {
    ...defaultCanvasBodyProps,
    dispatch: dispatchInDesigner,
    previewMode: buildPreviewMode(uiNodeState, components),
    ix3ElementTargets,
    ix2NodeMap,
    hrefMap,
    googleMapsApiKey,
    publicationId,
    databaseId,
    cssPropertyMap,
    timezone,
    shortName,
    siteId,
    components,
    rootExpression,
    pageId,
    assets,
    classNameMap,
    idAttrMap: buildIdAttrMap(components),
    siteBasePath,
    recaptchaRequired: Boolean(recaptchaRequired),
    isIndexed,
    pageHref,
    recaptchaSiteKey,
    collections,
    plugins,
    currentItemId,
    locales,
    paginationPageNumbers,
    currentVariationId,
    bodyEventHandlers,
    loadedHeadScripts,
    ecommPublishableKey: ecommPublishableKey || null,
    collectionId: collectionId || null,
    collectionIdToPageIdMap: collectionIdToPageIdMap || {},
    customNodeState: customNodeState || null,
    currentLocale,
    variantState: variantState || null,
    variantDataMap,
    variables,
    variableCollections,
    stylesMap,
    styleBlocks,
    breakpoints,
    mediaQuery: breakpointId,
    isOpenCanvasActive,
    apolloClient,
    apolloCacheResetCounter,
    ixState,
    workflow,
    dynamoData,
    entitlements,
    codeComponentVersion: codeComponentVersion || 0,
    functionsPreview: functionsPreview ?? null,
    frameOrientation,
    isComponentCanvasActive,
    globalComponentIds,
    annotationsMode: annotationsMode || false,
    quickCheckoutEnabled: quickCheckoutEnabled || false,
    ecommShippingZones: ecommShippingZones || undefined,
    ecommBusinessAddress: ecommBusinessAddress || undefined,
    ecommCurrencySettings: ecommCurrencySettings || undefined,
    cmsCollectionListPreviewEnabled: cmsCollectionListPreviewEnabled || false,
    cmsCollectionListPreviewItemsCount: cmsCollectionListPreviewItemsCount || 0,
    cmsNestedCollectionListPreviewItemsCount:
      cmsNestedCollectionListPreviewItemsCount || 0,
    componentLocalizations,
  };

  return (
    <ErrorBoundary
      onError={(error) => {
        console.error('Custom code error:', error);

        telemetrySendError({
          name: 'Custom code HTML Embed error',
          error,
        });
      }}
    >
      <CanvasBody {...canvasBodyProps} customCodeExecutionAllowed />
    </ErrorBoundary>
  );
};
